import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Prompts } from 'sprites-types/api/prompts'

export type MemorySection = 'prompt' | 'code'

interface MemoryState {
  prompts: Prompts | null
  editedPrompts: Prompts | null
  activeSection: MemorySection
  isPromptLoading: boolean
  isPromptLoaded: boolean
  isMemoryPanelShown: boolean
  selectedPromptId: number
}

export const initialState: MemoryState = {
  prompts: null,
  editedPrompts: null,
  activeSection: 'prompt',
  isPromptLoading: false,
  isPromptLoaded: false,
  isMemoryPanelShown: false,
  selectedPromptId: -1,
}

const memorySlice = createSlice({
  name: 'memory',
  initialState,
  reducers: {
    switchMemorySection: (state: MemoryState, action: PayloadAction<MemorySection>) => {
      state.activeSection = action.payload
    },
    setPromptLoading: (state: MemoryState, action: PayloadAction<boolean>) => {
      state.isPromptLoading = action.payload
    },
    setPromptLoaded: (state: MemoryState, action: PayloadAction<boolean>) => {
      state.isPromptLoaded = action.payload
    },
    setSelectedPromptId: (state: MemoryState, action: PayloadAction<number>) => {
      state.selectedPromptId = state.selectedPromptId === action.payload ? -1 : action.payload
    },
    setPrompts: (state: MemoryState, action: PayloadAction<Prompts | null>) => {
      state.prompts = action.payload
      // Once the normal prompts are set, we also have to set the editedPrompts to ensure they don't desync
      state.editedPrompts = action.payload
    },
    setEditedPrompts: (state: MemoryState, action: PayloadAction<Prompts>) => {
      state.editedPrompts = {
        ...state.editedPrompts,
        ...action.payload,
      }
    },
    clearEditedPrompts: (state: MemoryState) => {
      state.editedPrompts = state.prompts
    },
    toggleMemoryPanel: (state: MemoryState) => {
      state.isMemoryPanelShown = !state.isMemoryPanelShown
    },
    showMemoryPanel: (state: MemoryState, action: PayloadAction<boolean>) => {
      state.isMemoryPanelShown = action.payload
    },
  },
})

export const {
  switchMemorySection,
  setPromptLoading,
  setPromptLoaded,
  setSelectedPromptId,
  setPrompts,
  toggleMemoryPanel,
  showMemoryPanel,
  setEditedPrompts,
  clearEditedPrompts,
} = memorySlice.actions

export default memorySlice.reducer
